import React, { useState, useRef } from 'react';
import {
  Box, Button, Typography, Card, CardContent, Divider, ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  UploadFile as UploadIcon,
  FileDownloadDone as SqlIcon,
  OpenInNew as OpenInNewIcon,
  PlayArrow as ExecuteIcon
} from '@mui/icons-material';

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { useAuth } from '../Auth';
import api from '../api/axiosInstance';
import { useSnackbar } from '../context/SnackbarContext';

const STATE_CSV_SCHEMAS = {
  NSW: { label: 'NSW', portalUrl: null },
  VIC: {
    label: 'VIC',
    portalUrl: 'https://www.service.vic.gov.au/services/working-with-children-check-status-checker/transaction#bulkCheck'
  },
  QLD: { label: 'QLD', portalUrl: null },
  WA: { label: 'WA', portalUrl: null },
  SA: { label: 'SA', portalUrl: null },
  TAS: { label: 'TAS', portalUrl: null },
  ACT: { label: 'ACT', portalUrl: null },
  NT: { label: 'NT', portalUrl: null },
};

function GovFiles() {
  const { user } = useAuth();
  const [previewText, setPreviewText] = useState('');
  const [updates, setUpdates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [csvData, setCsvData] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [selectedState, setSelectedState] = useState('VIC');
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const tenantName = user?.tenant_name || '';
  const showSnackbar = useSnackbar();

  const handleStateChange = (event, newState) => {
    if (newState !== null) {
      setSelectedState(newState);
      setPreviewText('');
      setUpdates([]);
      setSummary(null);
      setCsvData('');
      setUploadedFileName('');
    }
  };

  const handleExportTenantCSV = async () => {
    if (!user?.tenant_id) return;

    try {
      const response = await api.get(
        `/gov-files/export?tenant_id=${user.tenant_id}&state=${selectedState}`,
        {
          responseType: 'blob'
        }
      );

      const disposition = response.headers['content-disposition'];
      let filename = `Gov_export_${user.schema_name || 'global'}_${selectedState}.csv`;

      if (disposition) {
        const filenameMatch = disposition.match(/filename=["']?([^"';]+)["']?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].trim();
        }
      }

      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export operation failed:', error);
      showSnackbar('Could not export file. Check the network tab for server error details.', 'error');
    }
  };

  const handleUploadResponseCSV = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadedFileName(file.name);
    const reader = new FileReader();

    reader.onload = async (e) => {
      const text = e.target.result;

      try {
        const response = await api.post('/gov-files/process-response', {
          csvData: text
        });

        setCsvData(text);
        setUpdates(response.data?.updates || []);
        setSummary(response.data?.summary || null);
        setPreviewText(response.data?.previewText || '');
        showSnackbar(
          `Parsed ${response.data?.summary?.total_parsed ?? 0} update(s) from government file.`,
          'success'
        );
      } catch (error) {
        console.error('Server reconciliation analysis failed:', error);
        const msg = error.response?.data?.error || 'Failed processing government response file on the server.';
        showSnackbar(msg, 'error');
      }
    };

    reader.readAsText(file);
    // Allow re-selecting the same file
    event.target.value = '';
  };

  const handleDownloadPreview = () => {
    if (!previewText) return;
    const blob = new Blob([previewText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `gov_apply_preview_${user?.schema_name || 'global'}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const previewRef = useRef(null);

  const handlePreviewKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
      if (previewRef.current) {
        e.preventDefault();
        const range = document.createRange();
        range.selectNodeContents(previewRef.current);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleApplyClick = () => {
    if (!csvData && updates.length === 0) return;
    setApplyConfirmOpen(true);
  };

  const handleConfirmApply = async () => {
    setApplyConfirmOpen(false);
    showSnackbar('Applying government file results...', 'info');

    try {
      // Prefer re-sending csvData so the server re-parses; never send raw SQL.
      const payload = csvData
        ? { operation: 'applyGovFileResults', csvData }
        : { operation: 'applyGovFileResults', updates };

      const response = await api.post('/gov-files/apply', payload);
      const matched = response.data?.matched_rows;
      const applied = response.data?.applied;
      showSnackbar(
        response.data?.message ||
          `Applied ${applied ?? 0} update(s); ${matched ?? 0} WWCC row(s) matched.`,
        'success'
      );
    } catch (error) {
      console.error('Gov file apply failed:', error);
      const errorMessage = error.response?.data?.error || 'Failed applying government file results.';
      showSnackbar(errorMessage, 'error');
    }
  };

  const hasPreview = Boolean(previewText) || updates.length > 0;

  return (
    <Box sx={{
      width: '95%',
      maxWidth: '1600px',
      mx: 'auto',
      mt: 3,
      mb: 6,
      height: 'auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
          Government Files
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Export registry payloads for screening submittals and apply verified government returns to WWCC records.
        </Typography>

        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            Select Authority Jurisdiction:
          </Typography>
          <ToggleButtonGroup
            color="primary"
            value={selectedState}
            exclusive
            size="medium"
            onChange={handleStateChange}
          >
            {Object.keys(STATE_CSV_SCHEMAS).map((stateKey) => (
              <ToggleButton key={stateKey} value={stateKey}>
                {stateKey}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Card variant="outlined" sx={{ width: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Step 1 & 2: Generate Registry Outbound Query
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Export your current local database records into a cleanly formatted template matching state specifications, then submit it to the government registry.
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {!user?.tenant_id ? (
                <Typography color="error" variant="body2" sx={{ fontWeight: 'bold' }}>
                  You are in Global View. Please choose a specific context tenant from the top menu before attempting a spreadsheet compile.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportTenantCSV}
                    sx={{ minWidth: '220px', py: 1.2 }}
                  >
                    Export Outbound CSV {tenantName ? `(${tenantName})` : ''}
                  </Button>

                  {STATE_CSV_SCHEMAS[selectedState]?.portalUrl && (
                    <Button
                      component="a"
                      href={STATE_CSV_SCHEMAS[selectedState].portalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      color="primary"
                      endIcon={<OpenInNewIcon />}
                      sx={{ minWidth: '220px', py: 1.2 }}
                    >
                      Open {selectedState} Submission Portal
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Card variant="outlined" sx={{ width: '100%' }}>
            <CardContent>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Step 4 & 5: Upload Government Verified Returns
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Provide the returned confirmation register sheet from your government portal. VeriSafe will preview the planned WWCC status updates, then apply them with parameterized database updates (no raw SQL).
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    component="label"
                    variant="outlined"
                    color="secondary"
                    startIcon={<UploadIcon />}
                    sx={{ minWidth: '220px', py: 1.2 }}
                  >
                    Upload Response CSV
                    <input type="file" accept=".csv" hidden onChange={handleUploadResponseCSV} />
                  </Button>

                  {uploadedFileName && (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'success.main' }}>
                      Loaded Target: {uploadedFileName}
                    </Typography>
                  )}
                </Box>

                {summary && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`Parsed: ${summary.total_parsed}`} color="primary" size="small" />
                    <Chip label={`Valid: ${summary.valid_count}`} color="success" size="small" />
                    <Chip label={`Invalid: ${summary.invalid_count}`} color="warning" size="small" />
                    <Chip label={`Skipped: ${summary.skipped}`} size="small" />
                  </Box>
                )}
              </Box>

              {hasPreview && (
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column' }}>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    mb: 1.5,
                    gap: 2
                  }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        Planned WWCC Updates (preview only):
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Review the calculated changes, then apply them. Raw SQL is no longer generated or accepted.
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                      <Button
                        size="medium"
                        variant="outlined"
                        color="primary"
                        startIcon={<ExecuteIcon />}
                        onClick={handleApplyClick}
                        sx={{ minWidth: '160px' }}
                      >
                        Apply Updates
                      </Button>

                      <Button
                        size="medium"
                        variant="contained"
                        color="primary"
                        startIcon={<SqlIcon />}
                        onClick={handleDownloadPreview}
                        sx={{ minWidth: '200px' }}
                      >
                        Download Preview
                      </Button>
                    </Box>
                  </Box>

                  <Box
                    ref={previewRef}
                    tabIndex={0}
                    onKeyDown={handlePreviewKeyDown}
                    sx={{
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      outline: 'none',
                      '&:focus': {
                        borderColor: 'primary.main',
                        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                      },
                      '& pre': {
                        margin: '0 !important',
                        overflow: 'visible !important'
                      }
                    }}
                  >
                    <SyntaxHighlighter
                      language="text"
                      style={tomorrow}
                      customStyle={{
                        fontSize: '0.875rem',
                        fontFamily: 'monospace',
                        padding: '16px'
                      }}
                    >
                      {previewText || 'No preview text available.'}
                    </SyntaxHighlighter>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Dialog open={applyConfirmOpen} onClose={() => setApplyConfirmOpen(false)}>
        <DialogTitle>Apply Government File Updates?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Apply {updates.length || 'these'} planned WWCC status update(s) for schema{' '}
            <strong>{user?.schema_name || 'global'}</strong>? Updates use fixed parameterized
            SQL templates only — raw SQL is not accepted.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setApplyConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmApply} color="primary" variant="contained">
            Confirm Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default GovFiles;
